/**
 * Customer booking + professional browsing API (backend routes/customer-app/booking.js).
 * Every price, slot and count comes from the server; nothing here is computed from constants.
 */
import { API_BASE_URL } from '../../config/api'
import { getToken, reportAuthFailure } from '../../utils/session'

const BASE = `${API_BASE_URL}/api/customer-app/booking`

async function call(url, options = {}) {
  const token = getToken()
  const res = await fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }), ...options.headers }
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    reportAuthFailure(res.status, data)
    const err = new Error(data.error || data.message || `Request failed (${res.status})`)
    err.status = res.status
    err.code = data.code || null   // 409: FEE_CHANGED (data.callOutFee = new fee) | SLOT_TAKEN | PROFESSIONAL_BUSY
    err.data = data
    throw err
  }
  return data
}

const qs = params => {
  const p = new URLSearchParams()
  Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p.append(k, v) })
  const s = p.toString()
  return s ? `?${s}` : ''
}

export const bookingApi = {
  trades: () => call(`${BASE}/trades`),
  services: trade => call(`${BASE}/trades/${encodeURIComponent(trade)}/services`),
  professionals: params => call(`${BASE}/professionals${qs(params)}`),
  professional: id => call(`${BASE}/professionals/${id}`),
  availability: (id, params) => call(`${BASE}/professionals/${id}/availability${qs(params)}`),
  quote: params => call(`${BASE}/quote${qs(params)}`),
  book: body => call(BASE, { method: 'POST', body: JSON.stringify(body) }),
  externalRequest: body => call(`${BASE}/external-request`, { method: 'POST', body: JSON.stringify(body) }),
  addresses: () => call(`${API_BASE_URL}/api/customers/addresses`)
}

/**
 * What a booking is for, carried through BookingDateTime → BookingReview → the booking itself, so the quote
 * and the booked fee use the case the assistant matched ({ issueType, severity }) rather than the pro's first
 * trade at moderate severity. The server normalises both (unknown severity → moderate) and returns what it
 * priced in quote.trade / quote.severity.
 */
export const bookingContextFrom = (criteria, professional) => {
  const trade = criteria?.issueType || professional?.trade || null
  const severity = criteria?.severity || null
  return { ...(trade && { trade }), ...(severity && { severity }) }
}

/** "Plumbing · severe": the trade and severity the server priced a quote (or booking.quote) for */
export const pricedForText = q => {
  if (!q) return ''
  const trade = q.trade ? q.trade.replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null
  return [trade, q.severity ? q.severity.toLowerCase() : null].filter(Boolean).join(' · ')
}

/** One line for a saved address row (customer_addresses) */
export const addressLine = a => [a?.address_line_1, a?.address_line_2, a?.city, a?.country].filter(Boolean).join(', ')

/** "35" or "32–50" for a { min, max } range, formatted with the money formatter */
export const moneyRange = (money, r) => (!r ? '—' : r.min === r.max ? money(r.min) : `${money(r.min)}–${money(r.max)}`)

/** Calendar-date key (YYYY-MM-DD) for a local Date, matching the server's market dates */
export const ymdOf = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/** Long date for a YYYY-MM-DD key without shifting it through a time zone */
export const formatYmd = (ymd, opts = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) => {
  if (!ymd) return ''
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', opts)
}

/** What happens to a paid call-out when the job goes ahead (the pro can mark it not deductible) */
export const callOutCreditText = deductible =>
  deductible ? 'Credited against the job if you go ahead' : 'Charged separately from the job'

/**
 * A refused booking the customer can act on (409): the server's message, and for FEE_CHANGED the new fee
 * to accept. Anything else → null (a plain error).
 */
export const bookingConflict = err =>
  err?.status === 409 ? { code: err.code, message: err.message, callOutFee: err.data?.callOutFee ?? null, currency: err.data?.currency || null } : null
